import {
  DataTableColDef,
  GridCellParams,
  GridColumnOrderChangeParams,
  GridColumnResizeParams,
  GridSortModel,
  GridValueFormatterParams,
} from '@chronosphereio/chrono-ui';
import { TimeFormat } from '@chronosphereio/core';
import { produce } from 'immer';
import { cloneDeep } from 'lodash';
import { useMemo } from 'react';
import { useTimeZone } from '@/components';
import {
  DataFrame,
  Field,
  FieldType,
  Override,
  ROW_ID_FIELD_NAME,
  TableOverrides,
  TableSort,
  TableSpec,
  UnitOptions,
} from '@/core';
import { mergeTimeSeries, TableCellWithLabelFilterButtons, useDataQueryResult } from '@/plugin-system';
import { useUserTimeFormat } from '@/plugin-system/runtime/useUserTimeFormat';
import { useTablePanelConfigStrict } from '../TableConfigProvider';
import {
  DEFAULT_TRANSFORM,
  getIconTextRenderer,
  getNumberRenderer,
  TableRow,
  useTransformedTimeSeries,
} from '../table-model';
import { formatTableTimestamp } from './format';
import { merge } from './merge';
import { normalizeValueFieldName } from './normalize';

const COL_MIN_WIDTH = 150;

export function isTableV2(spec: TableSpec) {
  return spec.transform === undefined;
}

/**
 * Returns the rows and columns for a table based on data from query results.
 */
export function useTableRowsAndColumns(spec: TableSpec) {
  // Modern table (TableV2) uses 'Table' data type
  const { queryResults: dataFrameResults, isLoading: isDataFrameLoading } = useDataQueryResult({
    dataTypeFilter: 'Table',
  });
  const { timeZone } = useTimeZone();
  const timeFormat = useUserTimeFormat();

  const dataFrameTable = useMemo(() => {
    const dataFrames: DataFrame[] = dataFrameResults.flatMap((result) => result?.data?.data || []);
    const mergedDataFrame = mergeDataFrames(dataFrames);
    return convertDataFrameToMUIDataTable(mergedDataFrame, timeFormat, timeZone, spec?.unit);
  }, [dataFrameResults, spec?.unit, timeZone, timeFormat]);

  // Legacy table uses 'TimeSeries' data type
  const { queryResults: timeSeriesResults, isLoading: isTimeSeriesLoading } = useDataQueryResult({
    dataTypeFilter: 'TimeSeries',
  });
  const data = mergeTimeSeries(timeSeriesResults);
  const timeSeriesTable = useTransformedTimeSeries(data, spec?.transform ?? DEFAULT_TRANSFORM, 0, {
    timeFormat,
    timeZone,
  });

  if (isTableV2(spec)) {
    return { ...dataFrameTable, isLoading: isDataFrameLoading };
  } else {
    return { ...timeSeriesTable, isLoading: isTimeSeriesLoading };
  }
}

function getFrameLengthByFields(fields: DataFrame['fields']) {
  const valueFrame = fields.find((field) => {
    return typeof field.values.length === 'number';
  });
  return valueFrame?.values.length ?? 0;
}

/**
 * Merges multiple data frames into one by joining on matching columns
 */
export function mergeDataFrames(dataFrames: DataFrame[]) {
  // Return early if there is only one data frame
  const firstDataFrame = dataFrames[0];
  if (dataFrames.length === 1 && firstDataFrame) {
    return firstDataFrame;
  }

  // We need to cloneDeep dataFrames here because this calls the Grafana merge function which modifies the underlying object.
  const dataFramesCopy = cloneDeep(dataFrames);

  const rowIdFieldName = 'rowId';

  // Create a new field to store a unique row ID for each row in a data frame.
  // This field will be used to join and merge the data frames.
  for (let frameIndex = 0; frameIndex < dataFramesCopy.length; frameIndex++) {
    const frame = dataFramesCopy[frameIndex];
    if (!frame) continue;

    const rowIdField: Field<string> = {
      name: rowIdFieldName,
      type: FieldType.string,
      values: new Array(frame.length),
    };

    // Loop through each row in the current data frame
    const firstField = frame.fields[0];
    if (!firstField) continue;
    for (let valueIndex = 0; valueIndex < firstField.values.length; valueIndex++) {
      // Construct a unique row ID by concatenating the values of all string fields in the row
      const labels = [];
      for (let i = 0; i < frame.fields.length; i++) {
        const field = frame.fields[i];
        if (field && field.type === FieldType.string) {
          labels.push(`${field.name}=${field.values[valueIndex]}`);
        }
      }

      const rowId = labels.join(', ');

      // assign the row id to the rowIdField
      rowIdField.values[valueIndex] = rowId;
    }

    // Add the new rowIdField to the current data frame's fields
    frame.fields.push(rowIdField);
  }

  // Merge the modified data frames
  const mergedDataFrames: DataFrame[] = merge(dataFramesCopy);

  // Filter out the 'rowId' field from the merged data frames
  const fields =
    mergedDataFrames[0]?.fields.filter((field) => {
      return field.name !== rowIdFieldName;
    }) ?? [];

  const dataFrame = {
    length: getFrameLengthByFields(fields),
    fields,
  };

  return sortDataFrameFields(dataFrame);
}

function StringCell({ value, field }: { value: string; field: Field }) {
  const columnName = field.name;
  const { onAddFilters } = useTablePanelConfigStrict();

  return (
    <TableCellWithLabelFilterButtons
      onAddFilters={onAddFilters}
      labelName={columnName}
      labelValue={value}
      formattedLabelValue={value ?? '--'}
    />
  );
}

/**
 * Converts one data frame to mui data table (rows and columns)
 */
export function convertDataFrameToMUIDataTable(
  dataFrame: DataFrame,
  timeFormat: TimeFormat,
  timeZone?: string | undefined,
  unit?: UnitOptions
) {
  const renderNumber = getNumberRenderer(unit);

  const timeFormatter = ({ value }: GridValueFormatterParams) => {
    return formatTableTimestamp({ value, timeZone, timeFormat });
  };

  let rowIdField: Field | undefined;
  const columns: DataTableColDef[] = [];
  for (let i = 0; i < dataFrame.fields.length; i++) {
    const field = dataFrame.fields[i];
    if (!field) continue;

    // Skip if the field name is "rowId".
    // We use this field to assign row IDs, and it should be hidden.
    if (field.name === ROW_ID_FIELD_NAME) {
      rowIdField = field;
      continue;
    }

    const flex = field.columnFlex ?? 1;
    if (field.type === FieldType.number) {
      columns.push({
        minWidth: COL_MIN_WIDTH,
        flex,
        field: field.name,
        headerName: field.name,
        renderCell: renderNumber,
        type: FieldType.number,
      });
    } else if (field.type === FieldType.time) {
      columns.push({
        minWidth: COL_MIN_WIDTH,
        flex,
        field: field.name,
        headerName: field.name,
        valueFormatter: timeFormatter,
        type: FieldType.time,
      });
    } else {
      const defaultCell = (params: GridCellParams) => {
        return <StringCell value={params.value as string} field={field} />;
      };

      let displayCell;
      if (field?.display) {
        displayCell = getIconTextRenderer(field.display);
      }

      columns.push({
        minWidth: COL_MIN_WIDTH,
        flex,
        field: field.name,
        headerName: field.name,
        renderCell: displayCell ?? defaultCell,
      });
    }
  }

  const firstField = dataFrame.fields[0];
  const numberOfRows = firstField ? firstField.values.length : dataFrame.length;
  const rows: TableRow[] = new Array(numberOfRows);

  for (let i = 0; i < numberOfRows; i++) {
    const row: TableRow = { _vfId: String(i) };

    for (let j = 0; j < dataFrame.fields.length; j++) {
      const field = dataFrame.fields[j];
      // Skip if the field name is "rowId".
      // We use this field to assign row IDs, and it should be hidden.
      if (!field || field.name === ROW_ID_FIELD_NAME) continue;
      const value = field.values[i];
      if (field.name && value !== undefined) {
        row['_vfId'] = `${row._vfId}-${value}`;
        row[field.name] = value;
      }
    }

    // use the id from rowId field if available
    if (rowIdField !== undefined) {
      row['_vfId'] = rowIdField.values[i];
    }
    rows[i] = row;
  }

  return { columns, rows };
}

interface UseTransformedColumnsArgs {
  columns: DataTableColDef[];
  columnOverrides?: Array<Override<TableOverrides>>;
}
/**
 * Returns the transformed columns after applying the column overrides.
 */
export function useTransformedColumns({ columns, columnOverrides }: UseTransformedColumnsArgs) {
  return useMemo(() => {
    return columns.map((col) => {
      // Ensure we do not mutate col in-place to avoid col width being stuck after resize
      const nextCol: DataTableColDef = {
        ...col,
        // Hide column menu until we figure out how to edit directly in table panel
        disableColumnMenu: true,
      };

      const override = findColumnOverrides(col.field, columnOverrides);
      if (!override) {
        return nextCol;
      }

      const { displayName, width } = override.properties;
      if (displayName) {
        nextCol.headerName = displayName;
      }

      // Apply width override explicitly
      if (width !== undefined) {
        nextCol.width = width;
        nextCol.flex = undefined;
        nextCol.minWidth = undefined;
      }

      return nextCol;
    });
  }, [columns, columnOverrides]);
}

interface HandleSortChangeArgs {
  newSort: GridSortModel;
  spec: TableSpec;
  onPluginSpecChange: (spec: TableSpec) => void;
}
export const handleTableSortChange = ({ spec, newSort, onPluginSpecChange }: HandleSortChangeArgs) => {
  onPluginSpecChange(
    produce(spec, (draft: TableSpec) => {
      draft.sort = newSort.map(
        (sort) =>
          ({
            sortBy: sort.field,
            sortOrder: sort.sort,
          }) as TableSort
      );
    })
  );
};

interface HandleTableColWidthChangeArgs {
  params: GridColumnResizeParams;
  spec: TableSpec;
  onPluginSpecChange: (spec: TableSpec) => void;
}
export const handleTableColWidthChange = ({ params, spec, onPluginSpecChange }: HandleTableColWidthChangeArgs) => {
  const {
    colDef: { field },
  } = params;

  const width: number | undefined = params.width === null || Number.isNaN(params.width) ? undefined : params.width;

  const widthOverride = {
    value: field,
    properties: {
      width: width,
    },
  };
  if (onPluginSpecChange) {
    onPluginSpecChange(
      produce(spec, (draft: TableSpec) => {
        if (draft.columnOverrides) {
          const overrideIndex = draft.columnOverrides.findIndex((override) => override.value === field);
          if (overrideIndex !== -1) {
            const columnOverride = draft.columnOverrides[overrideIndex];
            if (columnOverride) {
              columnOverride.properties.width = width;
            }
          } else {
            draft.columnOverrides.push(widthOverride);
          }
        } else {
          draft.columnOverrides = [widthOverride];
        }
      })
    );
  }
};

interface HandleTableColumnOrderArgs {
  params: GridColumnOrderChangeParams;
  columns: DataTableColDef[];
  spec: TableSpec;
  onPluginSpecChange: (spec: TableSpec) => void;
}
export const handleTableColumnOrderChange = ({
  params,
  columns,
  spec,
  onPluginSpecChange,
}: HandleTableColumnOrderArgs) => {
  const { column, oldIndex, targetIndex } = params;
  const order = columns.map((col) => col.field);
  // swap the old index with the new index
  const newOrder = order.slice();
  newOrder.splice(oldIndex, 1);
  newOrder.splice(targetIndex, 0, column.field);
  if (onPluginSpecChange) {
    onPluginSpecChange(
      produce(spec, (draft: TableSpec) => {
        draft.column_order = newOrder;
      })
    );
  }
};

export function sortDataFrameFields(df: DataFrame) {
  // All fields are sorted by type: time, string, number
  const getSortValue = (field: Field) => {
    switch (field.type) {
      case FieldType.time:
        return 0;
      case FieldType.string:
        return 1;
      case FieldType.number:
        return 2;
    }
    return Number.MAX_VALUE;
  };
  const fields = df.fields.sort((a, b) => {
    return getSortValue(a) - getSortValue(b);
  });
  return { ...df, fields };
}

export function findColumnOverrides(columnName: string, columnOverrides?: Array<Override<TableOverrides>>) {
  if (columnOverrides) {
    let overrideIndex = columnOverrides.findIndex((override) => override.value === columnName);

    if (overrideIndex < 0) {
      const normalized = normalizeValueFieldName(columnName);
      overrideIndex = columnOverrides.findIndex((override) => normalizeValueFieldName(override.value) === normalized);
    }

    return overrideIndex >= 0 ? columnOverrides[overrideIndex] : undefined;
  }

  return undefined;
}
